import React, { type ComponentPropsWithoutRef, forwardRef, useCallback, useState } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import type { NewCoreIconProps } from '@atlaskit/icon';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import WarningIcon from '@atlaskit/icon/core/migration/status-warning--warning';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';

import type {
	ToolbarButtonBaseProps,
	ToolbarButtonModalProps,
	ToolbarButtonProps,
	ToolbarPopupButtonProps,
} from './types';
import { useCombinedRefs } from './utils';

const styles = cssMap({
	hoverWrapper: {
		padding: '0',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.overlay'),
		'&:hover': {
			backgroundColor: token('elevation.surface.overlay.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.overlay.pressed'),
		},
	},
	actionDisabledWrapper: {
		backgroundColor: token('elevation.surface.overlay'),
	},
	disabledButtonWrapper: {
		backgroundColor: token('elevation.surface.overlay'),
		transition: 'none',
		opacity: token('opacity.disabled'),
	},
	diabledPopupContent: {
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
	},
	buttonContainerInMeatballMenu: {
		display: 'flex',
	},
});

const ButtonBase = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof Button>>(
	(props, ref) => <Button appearance="subtle" {...props} ref={ref} />,
);

const IconButtonBase = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof IconButton>>(
	(props, ref) => <IconButton appearance="subtle" {...props} ref={ref} />,
);

const ToolbarButtonBase = ({
	action,
	buttonProps,
	buttonRef,
	tabIndex,
	isInMeatballMenu,
}: ToolbarButtonBaseProps) => {
	const handleOnClick = useCallback(
		(
			e: React.MouseEvent<HTMLButtonElement>,
			analyticsEvent: UIAnalyticsEvent,
			buttonName: string,
			isDisabled: boolean,
		) => {
			fireUIAnalytics(analyticsEvent, 'bulkActionFloatingToolbar', {
				buttonName,
				isDisabled,
			});

			!action.isDisabled && 'onClick' in action && action.onClick?.(e, analyticsEvent);
			buttonProps?.onClick?.(e, analyticsEvent);
		},
		[action, buttonProps],
	);

	const getIconBefore = useCallback(
		(iconProps: NewCoreIconProps) => {
			if (!buttonProps?.isDisabled && action.isDisabled) {
				return <WarningIcon {...iconProps} color={token('color.icon.warning')} />;
			}
			return action.icon ? (
				<action.icon
					{...iconProps}
					color={token('color.icon')}
					primaryColor={token('color.icon')}
				/>
			) : undefined;
		},
		[action, buttonProps?.isDisabled],
	);

	const getButtonComponent = useCallback(() => {
		const actionText = <Text>{action.name}</Text>;

		if (action.iconOnly) {
			return (
				<IconButtonBase
					{...buttonProps}
					label={action.name}
					onClick={(...args) => {
						handleOnClick(...args, action.name, !!action.isDisabled);
					}}
					ref={buttonRef}
					tabIndex={tabIndex}
					isDisabled={buttonProps?.isDisabled}
					icon={(iconProps) =>
						action.icon ? (
							<action.icon
								{...iconProps}
								color={token('color.icon')}
								primaryColor={token('color.icon')}
							/>
						) : undefined
					}
					isTooltipDisabled={false}
				/>
			);
		}

		return (
			<ButtonBase
				{...buttonProps}
				onClick={(...args) => {
					handleOnClick(...args, action.name, !!action.isDisabled);
				}}
				ref={buttonRef}
				tabIndex={tabIndex}
				isDisabled={buttonProps?.isDisabled}
				iconBefore={getIconBefore}
				id={buttonProps?.id}
				shouldFitContainer={isInMeatballMenu && fg('responsive_bulk_ops_toolbar_platform')}
			>
				{isInMeatballMenu && fg('responsive_bulk_ops_toolbar_platform') ? (
					<Box as="span" xcss={styles.buttonContainerInMeatballMenu}>
						{actionText}
					</Box>
				) : (
					actionText
				)}
			</ButtonBase>
		);
	}, [action, buttonProps, buttonRef, handleOnClick, tabIndex, getIconBefore, isInMeatballMenu]);

	return (
		<Box
			testId="floating-toolbar.ui.toolbar-button.button"
			xcss={cx(
				styles.hoverWrapper,
				action.isDisabled && styles.actionDisabledWrapper,
				buttonProps?.isDisabled && styles.disabledButtonWrapper,
			)}
			ref={buttonProps?.ref}
		>
			{getButtonComponent()}
		</Box>
	);
};

const ToolbarPopupButton = ({
	action,
	isDisabled,
	buttonRef,
	tabIndex,
	isInMeatballMenu,
}: ToolbarPopupButtonProps) => {
	return (
		<Popup
			isOpen={action.isOpen}
			placement={
				isInMeatballMenu && action.placement && fg('responsive_bulk_ops_toolbar_platform')
					? action.placement
					: 'top'
			}
			onClose={action.onClose}
			trigger={(triggerProps) => (
				<ToolbarButtonBase
					action={action}
					buttonProps={{
						isDisabled,
						id: action.buttonId,
						...triggerProps,
						iconAfter:
							isInMeatballMenu && fg('responsive_bulk_ops_toolbar_platform')
								? ChevronRightIcon
								: undefined,
					}}
					buttonRef={buttonRef}
					tabIndex={tabIndex}
					isInMeatballMenu={isInMeatballMenu}
				/>
			)}
			label={action.name}
			content={action.content}
			role={action.role}
		/>
	);
};

const ToolbarModalButton = forwardRef<HTMLElement, ToolbarButtonModalProps>(
	({ action, isDisabled, buttonRef, tabIndex, isInMeatballMenu }, ref) => {
		const combinedRefs = useCombinedRefs(buttonRef, ref);
		const lowerCaseActionName = action.name.toLowerCase();

		return (
			<ToolbarButtonBase
				action={action}
				buttonProps={{
					isDisabled,
					interactionName: `bulk-operations.${lowerCaseActionName}.modal`,
				}}
				buttonRef={combinedRefs}
				tabIndex={tabIndex}
				isInMeatballMenu={isInMeatballMenu}
			/>
		);
	},
);

const ToolbarDisabledButton = ({ action, buttonRef, tabIndex }: ToolbarButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const toggleDialog = useCallback(() => {
		setIsOpen((oldState) => !oldState);
	}, [setIsOpen]);

	const onCloseDialog = useCallback(() => setIsOpen(false), [setIsOpen]);

	return (
		<Popup
			onClose={onCloseDialog}
			content={() => (
				<Box role="alert" xcss={styles.diabledPopupContent}>
					{action.disabledMessage}
				</Box>
			)}
			isOpen={isOpen}
			placement="top-start"
			testId="floating-toolbar.ui.toolbar-button.disabled-button-with-popup-inline-dialog"
			trigger={(triggerProps) => (
				<ToolbarButtonBase
					action={action}
					buttonProps={{
						onClick: toggleDialog,
						...triggerProps,
					}}
					buttonRef={buttonRef}
					tabIndex={tabIndex}
				/>
			)}
		/>
	);
};

export const ToolbarButton = ({
	action,
	isDisabled,
	buttonRef,
	tabIndex,
	isInMeatballMenu,
}: ToolbarButtonProps): React.JSX.Element => {
	if (!isDisabled && action.isDisabled) {
		return (
			<ToolbarDisabledButton
				action={action}
				isDisabled={isDisabled}
				buttonRef={buttonRef}
				tabIndex={tabIndex}
			/>
		);
	}

	if (action.type === 'popup') {
		return (
			<ToolbarPopupButton
				action={action}
				isDisabled={isDisabled}
				buttonRef={buttonRef}
				tabIndex={tabIndex}
				isInMeatballMenu={isInMeatballMenu && fg('responsive_bulk_ops_toolbar_platform')}
			/>
		);
	}

	if (action.type === 'modal') {
		return (
			<ModalTrigger
				entryPoint={action.entryPoint}
				entryPointParams={action.entryPointParams}
				entryPointProps={action.entryPointProps}
				modalProps={action.modalProps}
				title={action.title}
			>
				{({ ref }) => (
					<ToolbarModalButton
						action={action}
						isDisabled={isDisabled}
						buttonRef={buttonRef}
						tabIndex={tabIndex}
						ref={ref}
						isInMeatballMenu={isInMeatballMenu && fg('responsive_bulk_ops_toolbar_platform')}
					/>
				)}
			</ModalTrigger>
		);
	}

	return (
		<ToolbarButtonBase
			action={action}
			buttonProps={{ isDisabled }}
			buttonRef={buttonRef}
			tabIndex={tabIndex}
		/>
	);
};
